import React, { useRef, useEffect } from 'react';
import { Box, Chip, Typography, CircularProgress } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MarkdownMessage from './MarkdownMessage';

/**
 * Human-readable labels for MCP tool names.
 */
const TOOL_LABELS = {
  search_entity_notes: 'coach notes',
  get_leader_profile: 'leader profile',
  get_leaders: 'leaders',
  get_coach_assessments: 'assessments',
  get_coach_notes: 'coach notes',
  get_gof_assessment: 'growth assessment',
  get_report_content: 'report content',
  list_entity_reports: 'reports',
  list_entity_resources: 'documents',
  read_entity_resource: 'document content',
  search_sna_chunks: 'knowledge base',
  get_entity_sna_summary: 'network summary',
  search_care_resources: 'care resources',
  get_care_resources: 'care resources',
  perplexity_search: 'the web',
  disambiguate_person_for_search: 'person records',
  explore_entity_relationships: 'relationships',
  retrieve_relationship_context: 'relationship context',
  check_cross_entity_permissions: 'permissions',
  retrieve_fipd_documents: 'framework library',
  aggregate_temporal_data: 'trend data',
  toggle_note_taking: 'note settings',
};

const humanizeToolName = (name) => TOOL_LABELS[name] || name.replace(/_/g, ' ');

/**
 * Renders a streaming AI message with tool activity log and progressive text.
 * Used during SSE streaming to show tokens as they arrive.
 */
const StreamingMessage = ({
  content,              // Accumulated text so far
  isStreaming,          // Whether stream is active
  toolActivity = [],   // Array of { toolName, status: 'running'|'done', startedAt, completedAt }
}) => {
  const endRef = useRef(null);

  // Auto-scroll to bottom during streaming
  useEffect(() => {
    if (isStreaming && endRef.current) {
      endRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [content, isStreaming, toolActivity]);

  const hasToolActivity = toolActivity.length > 0;

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Tool activity log */}
      {hasToolActivity && (
        <Box sx={{ mb: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {toolActivity.map((tool, i) => (
            <Chip
              key={`${tool.toolName}-${tool.startedAt || i}`}
              icon={tool.status === 'running'
                ? <CircularProgress size={12} thickness={5} />
                : <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
              }
              label={tool.status === 'running'
                ? `Searching ${humanizeToolName(tool.toolName)}...`
                : `Found results in ${humanizeToolName(tool.toolName)}`
              }
              size="small"
              variant="outlined"
              color={tool.status === 'done' ? 'success' : 'primary'}
              sx={{
                alignSelf: 'flex-start',
                ...(tool.status === 'running' && {
                  animation: 'pulse 1.5s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.7 },
                  },
                }),
              }}
            />
          ))}
        </Box>
      )}

      {/* Message content with markdown */}
      {content ? (
        <MarkdownMessage content={content} />
      ) : (
        isStreaming && !hasToolActivity && (
          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            Thinking...
          </Typography>
        )
      )}

      {/* Blinking cursor */}
      {isStreaming && (
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            width: 2,
            height: '1.2em',
            backgroundColor: 'text.primary',
            ml: 0.25,
            verticalAlign: 'text-bottom',
            animation: 'blink 1s step-end infinite',
            '@keyframes blink': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0 },
            },
          }}
        />
      )}

      <div ref={endRef} />
    </Box>
  );
};

export default StreamingMessage;
